/**
 * The days the family remembers, and the pictures inside them.
 *
 * Three kinds of day sit on the timeline, and every one of them holds photos
 * and clips (`MemoryAnchor` in types.ts):
 *
 *   - **a milestone** — a wedding, an engagement, a brit, a bar or bat
 *     mitzvah, a new baby, a graduation, a new home — or an everyday event;
 *     one Moment record with `milestone` (or just `isEvent`) set;
 *   - **a story** somebody recorded;
 *   - **a birth**, the day a person came into the family.
 *
 * A **recipe** is filled the same way and is the one anchor here that is not a
 * day at all: it has no date and never appears on the timeline, but the thing
 * the family does to it — "here is my picture of it", "let me tell you what my
 * mother did differently" — is exactly what they do to a wedding, so it opens as
 * the same constellation and holds the same kind of record.
 *
 * Everything added to one of them is another Moment pointing back at it
 * through `addedTo`, so a wedding is a single line with as many pictures
 * inside it as the family cares to add — and each picture is still an ordinary
 * Moment, so it also shows in the Moments tab and under the circle of whoever
 * uploaded it.
 *
 * Nothing here is stored: it is all read off the Moments the app already has.
 */
import { byId, groupedBy, groupedByEach, pileOf } from '../lib/grouped';
import { momentDay } from './calendar';
import { momentAnchor, momentPeople } from './piles';
import type { FamilyPhoto, MemoryAnchor, MemorySubject } from './types';

/**
 * Everything that was added to this day, story, birth or recipe — in the list's
 * own order, taken-down ones included.
 *
 * "What hangs off this?" is asked of every picture the family has, once per row
 * a screen draws: the timeline asks how many pictures are in a day for each of
 * its lines, the shelf asks for a cover for each of its recipes. So the Moments
 * are sorted into piles once per list and the answer is read out of the pile
 * (lib/grouped, domain/piles). What a picture was added to never changes after
 * it is made, which is what makes this safe on the mock's own table too.
 */
export function addedTo(moments: FamilyPhoto[], anchor: MemoryAnchor): readonly FamilyPhoto[] {
  return pileOf(groupedBy(moments, momentAnchor), `${anchor.kind}:${anchor.id}`);
}

/** The two anchors name the same thing. */
export function sameAnchor(a: MemoryAnchor, b: MemoryAnchor): boolean {
  return a.kind === b.kind && a.id === b.id;
}

/** Reads and writes an anchor as one string, for a link or a query parameter. */
export function anchorToParam(a: MemoryAnchor): string {
  return `${a.kind}:${a.id}`;
}

export function anchorFromParam(s: string | null | undefined): MemoryAnchor | null {
  if (!s) return null;
  const at = s.indexOf(':');
  if (at < 1) return null;
  const kind = s.slice(0, at);
  const id = s.slice(at + 1);
  if (!id) return null;
  return kind === 'moment' || kind === 'story' || kind === 'birth' || kind === 'recipe' ? { kind, id } : null;
}

/** The milestone itself — a simcha, not one of the pictures inside it. */
export function isMilestone(m: FamilyPhoto): boolean {
  return !!m.milestone && !m.addedTo;
}

/** A Moment that is a day of its own (a simcha or an everyday event), rather than a picture inside one. */
export function isOwnDay(m: FamilyPhoto): boolean {
  return m.isEvent && !m.addedTo;
}

/** The day a milestone or event happened, or null when its date is unknown. */
export function milestoneDay(m: FamilyPhoto): string | null {
  return momentDay(m.takenOn)?.iso ?? null;
}

/**
 * Whether a milestone's day has arrived. A milestone the family has not
 * reached yet has nothing in it to look at and nobody could have added
 * anything, so it waits; one with no date at all never appears on the line.
 */
export function hasHappened(m: FamilyPhoto, today: string): boolean {
  const day = milestoneDay(m);
  return !!day && day <= today;
}

/**
 * The milestones that belong on the timeline: real milestones, with a date,
 * whose day has come — newest first.
 */
export function pastMilestones(moments: FamilyPhoto[], today: string): FamilyPhoto[] {
  return moments
    .filter((m) => !m.removed && isMilestone(m) && hasHappened(m, today))
    .sort((a, b) => (milestoneDay(b) ?? '').localeCompare(milestoneDay(a) ?? ''));
}

/**
 * Everything inside one day: for a milestone or an event its own picture comes
 * first (it is the one the family chose when they created it), then whatever
 * has been added since, newest first. A story and a birth have no picture of
 * their own, so they are only what the family added.
 *
 * Records without a picture or clip are left out, because there is nothing to
 * show.
 */
export function memoriesFor(moments: FamilyPhoto[], subject: MemorySubject): FamilyPhoto[] {
  /*
   * A person is not a day: nothing hangs off them, so what is theirs is
   * whatever names them — every picture, clip and recording their face is
   * tagged on, wherever it was added. Newest first by the day it happened
   * (falling back to the day it was uploaded), because a constellation fills
   * from the middle outwards and the most recent should be the nearest.
   */
  if (subject.kind === 'person') {
    return pileOf(groupedByEach(moments, momentPeople), subject.id)
      .filter((m) => !m.removed && !!m.storageKey)
      .sort((a, b) => momentWhen(b).localeCompare(momentWhen(a)));
  }
  const anchor = subject;
  const self = anchor.kind === 'moment' ? byId(moments).get(anchor.id) : undefined;
  const own = self && !!self.storageKey && !self.removed ? [self] : [];
  const added = addedTo(moments, anchor)
    .filter((m) => !!m.storageKey && !m.removed)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  return [...own, ...added];
}

/** When a Moment belongs in time: the day it records, or failing that the day it arrived. */
export function momentWhen(m: FamilyPhoto): string {
  return momentDay(m.takenOn)?.iso ?? m.createdAt.slice(0, 10);
}

/**
 * How many pictures and clips a day holds. Counted, not collected: the timeline
 * asks this for every line it draws, and putting a day's pictures in order to
 * find out how many there are is work nobody looks at.
 */
export function memoryCount(moments: FamilyPhoto[], subject: MemorySubject): number {
  if (subject.kind === 'person') return memoriesFor(moments, subject).length;
  const self = subject.kind === 'moment' ? byId(moments).get(subject.id) : undefined;
  let n = self && !!self.storageKey && !self.removed ? 1 : 0;
  for (const m of addedTo(moments, subject)) if (!!m.storageKey && !m.removed) n++;
  return n;
}

/** The Moment a picture was added to, when it was added to one. */
export function parentMoment(moments: FamilyPhoto[], m: FamilyPhoto): FamilyPhoto | undefined {
  return m.addedTo?.kind === 'moment' ? byId(moments).get(m.addedTo.id) : undefined;
}

/** True for a clip rather than a still picture. */
export function isVideo(m: FamilyPhoto): boolean {
  return !!m.mimeType?.startsWith('video/');
}

/** True for a voice note — somebody talking about the day rather than a picture of it. */
export function isAudio(m: FamilyPhoto): boolean {
  return !!m.mimeType?.startsWith('audio/');
}

